<template>
  <section class="validator-container">
    <div v-if="isLoading" class="loading-container">
      <b-loading :is-full-page="false" :active="isLoading"></b-loading>
      <p class="loading-text">Loading confirmation...</p>
    </div>

    <div v-else>
      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <!-- Success Screen -->
      <div v-if="isSubmitted" class="thank-you-section">
        <h2>Thank you! Confirmation recorded.</h2>
        <p>
          You’ve helped {{ user.firstName }} strengthen their professional profile.<br>
          Want to create your own Profile?
        </p>
        <router-link to="/" class="cta-link" v-if="this.$cookies.get('teamwrkr-token')">
          <b-button type="is-primary" class="create-statsheet-btn">
            Back Home &rarr;
          </b-button>
        </router-link>
        <router-link to="/welcome" class="cta-link" v-else>
          <b-button type="is-primary" class="create-statsheet-btn">
            Create Your Free Profile &rarr;
          </b-button>
        </router-link>
      </div>

      <!-- Validator Form -->
      <div v-if="!isSubmitted && !error" class="validator-form">
        <header class="validator-header">
          <h1 class="title">Confirm {{ user.firstName }}’s Profile</h1>
          <div class="validator-subtext">
            Please confirm two things. <strong>This is not a performance review.</strong>
          </div>
        </header>

        <!-- Link to user's public profile -->
      <div class="form-section statsheet-link-section">
        <router-link
          v-if="user && user._id"
          :to="`/profile/${user._id}`"
          class="public-statsheet-btn"
          target="_blank"
          rel="noopener"
        >
          <i class="mdi mdi-open-in-new"></i>
          View {{ user.firstName }}'s Public Profile
        </router-link>
      </div>

        <!-- Relationship Section -->
        <div class="form-section">
          <label class="section-label" for="relationship">How do you know {{ user.firstName }}?</label>
          <b-select
            id="relationship"
            v-model="form.relationship"
            placeholder="Select relationship"
            expanded
            required
          >
            <option disabled value="">Select relationship</option>
            <option>Worked together on the same team</option>
            <option>Managed/supervised them</option>
            <option>Cross-functional collaborator</option>
            <option>Client/vendor relationship</option>
            <option>Mentor/coach</option>
            <option>Other</option>
          </b-select>
        </div>

        <!-- Quick Confirmation Section -->
        <div class="form-section quick-confirm">
          <label class="section-label">Quick Confirmation</label>
          <b-checkbox v-model="form.knowsPerson" required>
            I know this person.
          </b-checkbox>
          <div class="helper-text">
            I can confirm I’ve interacted with {{ user.firstName }} professionally.
          </div>
          <b-checkbox v-model="form.isAccurate" required>
            This Profile is an accurate representation of their experience and skills.
          </b-checkbox>
          <div class="helper-text">
            To the best of my knowledge, what’s shown here is correct.
          </div>
        </div>

        <!-- Optional Note -->
        <div class="form-section">
          <label class="section-label" for="note">Add a short note (optional)</label>
          <b-input
            id="note"
            v-model="form.note"
            type="textarea"
            placeholder="E.g., “We worked together on Office 365 migrations; their summary looks accurate.”"
            rows="3"
          ></b-input>
        </div>

        <!-- Attestation -->
        <div class="attestation">
          <small>
            Your confirmation helps establish professional credibility. It is not a performance review or legal reference.
          </small>
        </div>

        <!-- Validation Message -->
        <div v-if="triedSubmit && !isFormValid" class="validation-message">
          Please complete all required fields to submit.
        </div>

        <!-- CTA Buttons -->
        <div class="cta-buttons">
          <b-button
            type="is-primary"
            class="submit-btn"
            :loading="isLoading"
            @click="handleSubmit(false)"
          >
            Confirm &amp; Submit
          </b-button>
          <button class="decline-link" type="button" @click="handleSubmit(true)">
            Decline
          </button>
        </div>

        <!-- Privacy Line -->
        <div class="privacy-line">
          <small>
            Note: Your name and relationship may appear as a verifier on {{ user.firstName }}’s profile. We never publish your email.
          </small>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Validator',
  data() {
    return {
      user: {},
      review: {},
      isLoading: true,
      isSubmitted: false,
      error: null,
      triedSubmit: false,
      form: {
        relationship: '',
        knowsPerson: false,
        isAccurate: false,
        note: ''
      }
    }
  },
  computed: {
    isFormValid() {
      return (
        this.form.relationship &&
        this.form.knowsPerson &&
        this.form.isAccurate
      );
    },
  },
  mounted() {
    this.getReview();
  },
  methods: {
    async getReview() {
      console.log('running get review')
      try {
        const response = await this.axios.get(
          `${process.env.VUE_APP_API_URL}/users/review/${this.$route.params.id}`,
          {
            headers: {
              Authorization: this.$cookies.get('teamwrkr-token')
            }
          }
        );
        this.user = response.data.user;
        this.review = response.data.review;
        this.isLoading = false;

        if (this.review.status === 'completed' || this.review.status === 'declined') {
          this.$router.push('/home');
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          this.error = err?.response?.data?.message || 'Unauthorized: Please log in again.';
        } else {
          this.error = err?.response?.data?.message || 'Could not retrieve data. Please try again.';
        }
        this.isLoading = false;
      }
    },
    async handleSubmit(isDecline) {
      this.triedSubmit = false;

      let payload = {};
      if (isDecline) {
        payload.status = 'declined';
        payload.note = this.form.note;
        // if (this.form.knowsPerson === false && this.form.note.trim() === "") payload.note = 'I do not know this person.';
      } else {
        payload.status = 'completed';
        payload.relationship = this.form.relationship;
        payload.isAccurate = this.form.isAccurate;
        payload.note = this.form.note;
        this.triedSubmit = true;
        if (!this.isFormValid) return;
      }


      console.log('form submit', payload);
      try {
        this.isLoading = true;
        await this.axios.patch(
          `${process.env.VUE_APP_API_URL}/users/review/${this.$route.params.id}`,
          payload,
          {
            headers: {
              Authorization: this.$cookies.get('teamwrkr-token')
            }
          }
        );
        this.isSubmitted = true;
        this.$buefy.toast.open({
          message: 'Thank you — confirmation recorded.',
          type: 'is-success'
        });
      } catch (err) {
        this.error = err?.response?.data?.message || 'Could not send confirmation. Please try again.';
      } finally {
        this.isLoading = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.statsheet-link-section {
  margin-bottom: 0.7rem;
  display: flex;
  justify-content: center; // Center the button horizontally
  .public-statsheet-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    background: $color-primary;
    color: $color-surface !important;
    font-weight: 700;
    font-size: 1.08rem;
    border-radius: 2rem;
    padding: 0.7rem 1.5rem;
    text-decoration: none;
    box-shadow: 0 2px 8px rgba(170, 118, 214, 0.08);
    transition: background 0.18s, color 0.18s, border 0.18s;
    cursor: pointer;
    border: 2px solid $color-primary;
    i {
      font-size: 1.2rem;
      margin-right: 0.5rem;
      color: $color-surface;
      transition: color 0.18s;
    }
    &:hover {
      background: $color-surface;
      color: $color-primary !important;
      border: 2px solid $color-primary;
      i {
        color: $color-primary;
      }
    }
  }
}

.validator-container {
  padding: 2rem;
  background-color: $color-surface-soft;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;

    .loading-text {
      margin-top: 1.5rem;
      font-size: 1.1rem;
      color: $color-text-subtle;
    }
  }

  .validator-form {
    background: $color-surface;
    border-radius: 1rem;
    box-shadow: 0 2px 12px rgba(170, 118, 214, 0.07);
    padding: 2.2rem 2rem 1.5rem 2rem;
    max-width: 540px;
    width: 100%;
    margin: 2rem auto 0 auto;
    display: flex;
    flex-direction: column;
    gap: 1.3rem;
  }

  .validator-header {
    text-align: center;
    margin-bottom: 0.5rem;
    .title {
      font-size: 1.5rem;
      font-weight: 800;
      color: $color-primary;
      margin-bottom: 0.3rem;
    }
    .validator-subtext {
      font-size: 1.08rem;
      color: $color-text;
      margin-bottom: 0.2rem;
      .not-review {
        color: $color-primary;
        font-weight: 600;
      }
    }
  }

  .form-section {
    margin-bottom: 0.7rem;
    .section-label {
      font-size: 1.08rem;
      font-weight: 700;
      color: $color-text;
      margin-bottom: 0.3rem;
      display: block;
    }
    .b-select,
    .b-input {
      margin-top: 0.3rem;
    }
  }

  .quick-confirm {
    .b-checkbox {
      margin-bottom: 0.2rem;
      font-weight: 600;
      color: $color-info;
    }
    .helper-text {
      font-size: 0.97rem;
      color: $color-text-subtle;
      margin-bottom: 0.6rem;
      margin-left: 2rem;
    }
  }

  .attestation {
    font-size: 0.95rem;
    color: $color-text-subtle;
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    text-align: left;
  }

  .cta-buttons {
    display: flex;
    align-items: center;
    gap: 1.2rem;
    margin-top: 0.5rem;

    .submit-btn {
      font-weight: 700;
      font-size: 1.08rem;
      padding: 0.7rem 1.5rem;
    }
    .decline-link {
      background: none;
      border: none;
      color: $color-primary;
      font-size: 1.08rem;
      font-weight: 600;
      text-decoration: underline;
      cursor: pointer;
      padding: 0.7rem 0.7rem;
      transition: color 0.18s;
      &:hover {
        color: $color-accent;
      }
    }
  }

  .privacy-line {
    margin-top: 1.2rem;
    text-align: left;
    font-size: 0.92rem;
    color: $color-text-subtle;
  }

  .thank-you-section {
    background: $color-surface;
    border-radius: 1rem;
    box-shadow: 0 2px 12px rgba(170, 118, 214, 0.07);
    padding: 2.2rem 2rem 1.5rem 2rem;
    max-width: 540px;
    width: 100%;
    margin: 2rem auto 0 auto;
    text-align: center;
    h2 {
      color: $color-primary;
      font-size: 1.4rem;
      font-weight: 800;
      margin-bottom: 1rem;
    }
    // .cta-link {
    //   margin-top: 1.2rem;
    //   display: inline-block;
    //   font-size: 1.08rem;
    //   font-weight: 700;
    //   color: $color-surface;
    //   border-radius: 0.5rem;
    //   padding: 0.7rem 1.5rem;
    //   text-decoration: none;
    //   transition: background 0.18s;
    //   &:hover {
      //     background: $color-accent;
      //     color: $color-surface;
      //   }
      // }
      .create-statsheet-btn {
        margin-top: 1.2rem;
        background: $color-primary;
    }
  }

  .decline-modal-content {
    text-align: center;
    padding: 1.5rem 0.5rem;
    h3 {
      font-size: 1.15rem;
      font-weight: 700;
      color: $color-primary;
      margin-bottom: 0.7rem;
    }
    p {
      color: $color-text-muted;
      margin-bottom: 1.2rem;
    }
    .b-button {
      font-weight: 700;
      font-size: 1.08rem;
      padding: 0.7rem 1.5rem;
    }
  }

  .validation-message {
    color: $color-accent;
    font-weight: 600;
    text-align: center;
    margin-top: 0.5rem;
    font-size: 1.05rem;
  }

  .error-message {
    color: $color-accent;
    font-weight: 600;
    text-align: center;
    margin-top: 2rem;
    font-size: 1.1rem;
  }
}
</style>
